import React from "react";
import { Car, Shield, CheckCircle, Sparkles, Smartphone } from "lucide-react";
import { RegistrationInputButton } from "@/components/valuation/RegistrationInputButton";
import { AppStoreBadges } from "@/components/index/AppStoreBadges";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { indexLabels } from "@/i18n";

interface HeroSectionProps {
  onValuationClick: () => void;
}

const HeroSection = ({ onValuationClick }: HeroSectionProps) => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const labels = indexLabels(language);
  const isDark = theme === "dark";

  return (
    <section className="relative overflow-hidden">
      <div>
        <div className="relative bg-background rounded-3xl p-6 md:p-8">
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              padding: "2px",
              background: "linear-gradient(135deg, #14b8a6, #d4a574, #f59e0b)",
              WebkitMask:
                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500/10 to-amber-500/10 backdrop-blur-sm rounded-full mb-4 border border-teal-500/30">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-foreground">
                  {labels.hero.banner}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight text-foreground">
                {labels.hero.headlinePart1}
                <span className="bg-gradient-to-r from-teal-500 via-teal-400 to-amber-500 bg-clip-text text-transparent">
                  {labels.hero.headlinePart2}
                </span>
              </h1>

              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-lg">
                {labels.hero.description}
                <span className="font-semibold bg-gradient-to-r from-teal-500 to-amber-500 bg-clip-text text-transparent">
                  {labels.hero.descriptionHighlight}
                  {labels.hero.descriptionAfter ?? "."}
                </span>
              </p>

              <div className="flex flex-wrap gap-2">
                {[
                  { icon: Shield, text: labels.hero.trustBadge1 },
                  { icon: CheckCircle, text: labels.hero.trustBadge2 },
                  { icon: Car, text: labels.hero.trustBadge3 },
                ].map((item) => (
                  <div
                    key={item.text}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-teal-500/5 to-amber-500/5 rounded-lg border border-teal-500/20"
                  >
                    <item.icon className="w-3.5 h-3.5 text-teal-500" />
                    <span className="text-xs text-foreground">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="relative group">
                <div
                  className={`absolute -inset-0.5 rounded-2xl blur-sm ${isDark ? "bg-white/10" : "bg-black/10"}`}
                />

                <div
                  className={`relative bg-gradient-to-br dark:from-teal-900/85 dark:to-amber-900/85 from-teal-500 to-amber-500 rounded-2xl p-5 md:p-6 border-4 ${isDark ? "border-black" : "border-white"} transition-colors duration-150 cursor-pointer ${
                    isDark
                      ? "shadow-[0_10px_30px_-5px_rgba(255,255,255,0.2),0_4px_12px_-2px_rgba(255,255,255,0.1)] group-hover:shadow-[0_15px_40px_-5px_rgba(255,255,255,0.25),0_6px_16px_-2px_rgba(255,255,255,0.15)]"
                      : "shadow-[0_10px_30px_-5px_rgba(0,0,0,0.2),0_4px_12px_-2px_rgba(0,0,0,0.1)] group-hover:shadow-[0_15px_40px_-5px_rgba(0,0,0,0.25),0_6px_16px_-2px_rgba(0,0,0,0.15)]"
                  }`}
                  onClick={onValuationClick}
                >
                  <div className="text-center mb-4 relative z-10">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mb-3 group-hover:bg-white/30 transition-colors duration-150">
                      <Car className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                      {labels.valuation.title}
                    </h3>
                    <p className="text-white/80 text-xs">
                      {labels.valuation.subtitle}
                    </p>
                  </div>

                  <div className="space-y-3 relative z-10">
                    <RegistrationInputButton onClick={onValuationClick} />

                    <div className="flex items-center justify-center gap-3 pt-3 border-t border-white/20">
                      <div className="text-center">
                        <p className="text-lg md:text-xl font-bold text-white">
                          {labels.valuation.free}
                        </p>
                        <p className="text-[10px] text-white/70">
                          {labels.valuation.valuation}
                        </p>
                      </div>
                      <div className="w-px h-6 bg-white/30" />
                      <div className="text-center">
                        <p className="text-lg md:text-xl font-bold text-white">
                          10,000+
                        </p>
                        <p className="text-[10px] text-white/70">
                          {labels.valuation.satisfiedCustomers}
                        </p>
                      </div>
                      <div className="w-px h-6 bg-white/30" />
                      <div className="text-center">
                        <p className="text-lg md:text-xl font-bold text-white">
                          {labels.valuation.fast}
                        </p>
                        <p className="text-[10px] text-white/70">
                          {labels.valuation.response}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!isNative && (
            <div className="relative z-10 mt-6 md:mt-8 pt-6 border-t border-teal-500/15">
              <div className="flex flex-col gap-4 rounded-2xl border border-transparent bg-gradient-to-r from-teal-500/[0.06] via-transparent to-amber-500/[0.06] p-4 dark:border-teal-500/25 dark:from-teal-500/14 dark:via-teal-500/6 dark:to-amber-500/12 dark:shadow-[inset_0_1px_0_0_rgba(45,212,191,0.1)] sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-5">
                <div className="flex items-center gap-3 sm:min-w-0 sm:flex-1">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-teal-500/25 bg-gradient-to-br from-teal-500/15 to-amber-500/15 dark:border-teal-400/40 dark:from-teal-500/30 dark:to-amber-500/20 dark:shadow-[0_0_20px_-4px_rgba(45,212,191,0.45)] sm:h-11 sm:w-11">
                    <Smartphone className="h-5 w-5 text-teal-500 dark:text-teal-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-snug text-foreground md:text-base">
                      {labels.hero.appDownloadTitle}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:max-w-md md:text-sm">
                      {labels.hero.appDownloadSubtitle}
                    </p>
                  </div>
                </div>

                <div className="w-full sm:w-auto sm:shrink-0">
                  <AppStoreBadges
                    appStoreLabel={labels.hero.appStoreLabel}
                    appStoreName={labels.hero.appStoreName}
                    playStoreLabel={labels.hero.playStoreLabel}
                    playStoreName={labels.hero.playStoreName}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
